import React, { useRef, useEffect, useState } from "react";
import Webcam from "react-webcam";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  BulbOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Modal, Button, Typography, Card, List, Alert } from "antd";
import { useFaceLiveness } from "../../../utils/hooks/useFaceLiveness";
import { useVoiceInstructions } from "../../../utils/hooks/useVoiceInstructions";
import { useTenant } from "../../../store/context/tenant-context";
import { useNotification } from "../../../store/context/notification-context";
import { postAuthenticatedRequest } from "../../../utils/api/requests/request";
import {
  getSessionData,
  setSessionData,
} from "../../../utils/interceptors/localStorageService";

const { Text } = Typography;

const FaceLivenessScanner = ({ open, onClose, onCompleted }) => {
  const tenant = useTenant();
  const webcamRef = useRef(null);
  const { state, start, resetLiveness, submissionPayload } =
    useFaceLiveness(webcamRef);
  const { speak } = useVoiceInstructions();
  const [hasStarted, setHasStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cameraAccessError, setCameraAccessError] = useState(null);
  const { triggerNotification } = useNotification();
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (state.isActive) {
      const instruction =
        state.step === "ALIGN" && !state.isFaceCentered
          ? "Center your face in the frame"
          : state.instruction;

      console.log(`[Scanner] Triggering voice: "${instruction}"`);
      speak(instruction);
    }
  }, [state.step, state.isActive, state.isFaceCentered, speak]);

  const handleStart = () => {
    console.log("Start button clicked - triggering voice");
    speak("Verification started. Please align your face.");
    setHasStarted(true);
    start();
  };

  const handleRetake = () => {
    // Restart from the first challenge without leaving the camera screen.
    resetLiveness();
    hasSubmittedRef.current = false;
    setCameraAccessError(null);
    setHasStarted(true);
    speak("Retake started. Please align your face.");
    start();
  };

  const reset = () => {
    resetLiveness();
    setHasStarted(false);
    hasSubmittedRef.current = false;
    setCameraAccessError(null);
  };

  const handleCameraError = (err) => {
    setCameraAccessError(err || new Error("Unable to access camera"));
    resetLiveness();
    setHasStarted(false);
    hasSubmittedRef.current = false;
  };

  const isSuccess = state.step === "SUCCESS";

  const handleSubmit = async () => {
    if (!open) return;
    if (!submissionPayload) return;
    if (hasSubmittedRef.current) return;

    hasSubmittedRef.current = true;
    setIsSubmitting(true);

    try {
      console.log("submissionPayload", submissionPayload);
      const response = await postAuthenticatedRequest(
        "SubmitLivenessCheckCommand",
        submissionPayload,
      );
      const result = response?.outData;

      if (result?.isSuccessful) {
        triggerNotification({
          type: "success",
          message: "Verification submitted",
          description: result?.message || "",
        });

        // Optimistic local session update so the dashboard can hide the banner.
        const existingSession = getSessionData() || {};
        setSessionData({
          ...existingSession,
          hasCompletedLivenessCheck: true,
          livenessCheckDate:
            submissionPayload?.metadata?.completedAt ||
            new Date().toISOString(),
        });

        onCompleted?.();
        reset();
        onClose?.();
      } else {
        triggerNotification({
          type: "error",
          message: "Liveness check submission failed",
          description: result?.message || "Please try again.",
        });
        hasSubmittedRef.current = false;
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Liveness check submission failed",
        description: error?.message || "Please try again.",
      });
      hasSubmittedRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const instructions = [
    {
      icon: <EyeOutlined style={{ fontSize: 16 }} />,
      title: "Keep your face inside the guide",
      description: "Look directly at the camera and keep your head centered.",
    },
    {
      icon: <BulbOutlined style={{ fontSize: 16 }} />,
      title: "Use a bright environment",
      description:
        "Avoid dark rooms, strong backlight, and heavy shadows on your face.",
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: 16 }} />,
      title: "Remove anything blocking your face",
      description:
        "Take off caps, masks, or anything that hides your eyes or cheeks.",
    },
    {
      icon: <SyncOutlined style={{ fontSize: 16 }} />,
      title: "Follow the live prompts naturally",
      description:
        "You may be asked to blink, turn, nod, or smile before verification completes.",
    },
  ];

  return (
    <Modal
      open={!!open}
      onCancel={() => {
        reset();
        onClose?.();
      }}
      footer={null}
      centered
      width={720}
      destroyOnClose
      maskClosable={false}
      title={<p className="text-center">Liveness Check</p>}
    >
      {cameraAccessError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Camera access required"
          description="Please connect a camera and allow camera permissions in your browser settings, then start verification again."
        />
      )}
      {/* Step 1: Instructions before camera starts */}
      {!hasStarted && (
        <div className="flex flex-col gap-4 pt-2">
          <div>
            <Text style={{ color: tenant.theme.colorPrimary }} strong>
              Before You Start
            </Text>
            <div className="mt-1">
              <Text type="secondary">
                These steps reduce failed attempts and help the detector finish
                cleanly.
              </Text>
            </div>
          </div>
          <Card bodyStyle={{ padding: "12px 0" }}>
            <List
              size="small"
              dataSource={instructions}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{ color: tenant.theme.colorPrimary }}
                        className="flex justify-center items-center w-8 h-8 rounded-lg border border-gray-100"
                      >
                        {item.icon}
                      </div>
                    }
                    title={
                      <Text
                        style={{ color: tenant.theme.colorPrimary }}
                        className="text-sm font-semibold"
                      >
                        {item.title}
                      </Text>
                    }
                    description={
                      <Text type="secondary" className="text-xs">
                        {item.description}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          <div className="flex justify-center mt-4">
            <Button
              type="primary"
              size="large"
              onClick={handleStart}
              className="px-4 py-2 w-full max-w-md h-full text-base font-semibold shadow-none"
            >
              Start Verification
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Camera & liveness flow */}
      {!isSuccess && hasStarted && (
        <div className="flex flex-col gap-4 items-center py-2">
          <p
            style={{ color: tenant.theme.colorPrimary }}
            className={`text-sm md:text-base transition-all duration-200 ${
              state.isActive ? "font-semibold text-gray-900" : "text-gray-600"
            }`}
          >
            {state.isActive
              ? state.instruction
              : "Ready to begin your session?"}
          </p>

          <div className="overflow-hidden relative w-72 h-72 bg-black rounded-3xl border border-gray-100 shadow-md md:w-80 md:h-80">
            <Webcam
              ref={webcamRef}
              audio={false}
              className="object-cover w-full h-full -scale-x-100"
              videoConstraints={{ facingMode: "user" }}
              // onUserMediaError={handleCameraError}
            />

            {/* SVG Overlay for Oval */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 400 400"
            >
              <defs>
                <mask id="overlay-mask">
                  <rect width="100%" height="100%" fill="white" />
                  <ellipse cx="200" cy="200" rx="100" ry="140" fill="black" />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(15,23,42,0.75)"
                mask="url(#overlay-mask)"
              />
              <ellipse
                cx="200"
                cy="200"
                rx="100"
                ry="140"
                fill="none"
                stroke={
                  state.isFaceCentered && state.isActive ? "#10b981" : "#ffffff"
                }
                strokeWidth="4"
                className={
                  state.isFaceCentered && state.isActive ? "animate-pulse" : ""
                }
              />
            </svg>

            {/* {!state.isActive && (
              <div className="flex absolute inset-0 z-10 flex-col gap-4 justify-center items-center backdrop-blur-md bg-slate-900/85">
                <Button
                  type="primary"
                  size="large"
                  onClick={handleStart}
                  className="px-6 py-2 font-semibold rounded-xl shadow-md"
                >
                  Start Verification
                </Button>
              </div>
            )} */}

            {state.step === "FAILED" && (
              <div className="flex absolute inset-0 z-10 flex-col gap-3 justify-center items-center text-red-400 backdrop-blur-xl">
                <CloseCircleOutlined style={{ fontSize: 64 }} />
                <h2 className="text-xl font-semibold">Verification Failed</h2>
                <Button type="primary" onClick={reset}>
                  <ReloadOutlined style={{ fontSize: 18 }} /> Retry
                </Button>
              </div>
            )}
          </div>

          <div className="overflow-hidden mt-4 w-full max-w-md h-2 bg-gray-100 rounded-full">
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${state.progress}%`,
                backgroundColor: tenant.theme.colorPrimary,
              }}
            />
          </div>
        </div>
      )}

      {/* Step 2 success UI (no webcam/progress) */}
      {isSuccess && hasStarted && (
        <div className="flex flex-col gap-4 items-center py-2">
          <p
            style={{ color: tenant.theme.colorPrimary }}
            className="text-sm font-semibold md:text-base"
          >
            Verification Successful
          </p>

          {submissionPayload?.imageBase64 ? (
            <div className="overflow-hidden w-48 h-48 bg-black rounded-2xl border shadow-sm border-white/40">
              <img
                src={submissionPayload.imageBase64}
                alt="Captured face"
                className="object-cover w-full h-full"
              />
            </div>
          ) : (
            <p className="text-xs text-slate-100/90">
              Captured image preview unavailable.
            </p>
          )}

          <div className="flex gap-2 mt-2">
            <Button onClick={handleRetake} disabled={isSubmitting}>
              Retake
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!submissionPayload}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default FaceLivenessScanner;
